import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, TrendingUp, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function Profile({ user }) {
  const [userLevel, setUserLevel] = useState(null);
  const [progress, setProgress] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [levelRes, progressRes] = await Promise.all([
          axios.get(`${API}/users/level`, { withCredentials: true }),
          axios.get(`${API}/users/progress`, { withCredentials: true })
        ]);
        setUserLevel(levelRes.data);
        setProgress(progressRes.data);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };
    fetchData();
  }, []);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="min-h-screen">
      <Navigation user={user} />

      <main className="container mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-8">
            <div className="flex items-start gap-6">
              <Avatar className="h-24 w-24 rounded-none" data-testid="profile-avatar">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="rounded-none text-2xl">
                  {user?.name ? getInitials(user.name) : 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-none mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="profile-name">
                  {user?.name}
                </h1>
                <p className="text-lg text-muted-foreground mb-4">{user?.email}</p>
                <div className="inline-block px-3 py-1 text-xs uppercase tracking-wide bg-muted rounded-none">
                  {user?.role === 'student' ? 'Aluno' : user?.role === 'admin' ? 'Administrador' : 'Super Admin'}
                </div>
              </div>
            </div>
          </div>

          {userLevel && (
            <div className="md:col-span-4">
              <div className="tech-card-shadow p-6 rounded-none border border-white/5 relative corner-accent" data-testid="profile-level-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 text-primary">
                    <Award className="h-5 w-5" />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Expertise_Level</div>
                </div>
                <div className="text-4xl font-bold font-heading text-white mb-2 uppercase">{userLevel.level}</div>
                <div className="text-sm font-mono text-primary/80 mb-6">{userLevel.tools_rated} ferramentas avaliadas</div>

                <div className="pt-6 border-t border-white/10">
                  <div className="flex items-end justify-between mb-2">
                    <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Pontuação Geral</span>
                    <span className="text-2xl font-bold font-mono text-primary">{userLevel.score}%</span>
                  </div>
                  <div className="w-full bg-white/5 h-1.5 overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-1000"
                      style={{ width: `${userLevel.score}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <Card className="p-6 rounded-none border border-border" data-testid="stat-modules">
            <User className="h-8 w-8 mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-bold mb-1">{progress.length}</div>
            <div className="text-sm text-muted-foreground uppercase tracking-wide">Módulos Iniciados</div>
          </Card>

          <Card className="p-6 rounded-none border border-border" data-testid="stat-tools">
            <TrendingUp className="h-8 w-8 mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-bold mb-1">{userLevel?.tools_rated || 0}</div>
            <div className="text-sm text-muted-foreground uppercase tracking-wide">Ferramentas Avaliadas</div>
          </Card>

          <Card className="p-6 rounded-none border border-border" data-testid="stat-score">
            <Award className="h-8 w-8 mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-bold mb-1">{userLevel?.score || 0}%</div>
            <div className="text-sm text-muted-foreground uppercase tracking-wide">Conhecimento em IA</div>
          </Card>
        </div>

        <div>
          <h2 className="text-3xl font-bold tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>Seu Progresso</h2>
          {progress.length > 0 ? (
            <div className="space-y-4">
              {progress.map((item) => (
                <Card key={item.progress_id} className="p-6 rounded-none border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Módulo {item.module_id}</h3>
                      <p className="text-sm text-muted-foreground">
                        {item.completed_activities.length} atividades completadas
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">{Math.round(item.progress_percentage)}%</div>
                      <div className="text-xs text-muted-foreground uppercase">Progresso</div>
                    </div>
                  </div>
                  <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-500"
                      style={{ width: `${item.progress_percentage}%` }}
                    />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 rounded-none border border-border text-center">
              <p className="text-muted-foreground">Você ainda não iniciou nenhum módulo. Começe agora sua jornada AI-First!</p>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}